import http from "../http-common";
import {
  IAccountManagementAssignableMember,
  IAccountManagementCustomer,
  IAccountManagementCustomerNote,
  IAccountManagementCustomerPaginatedResponse,
  IAccountManagementOrderUpdate,
  IAccountManagementOrderUpdateResponse,
  IAccountManagementProfitLoss,
  IAccountManagementProfitLossFilter,
} from "../types/AccountManagementCustomer";

const toProfitLossParams = (filter?: IAccountManagementProfitLossFilter) => {
  if (!filter) return undefined;
  const params: Record<string, string | boolean> = {};
  if (filter.purchaseDateFrom) params.purchaseDateFrom = filter.purchaseDateFrom;
  if (filter.purchaseDateTo) params.purchaseDateTo = filter.purchaseDateTo;
  if (filter.orderStatus) params.orderStatus = filter.orderStatus;
  if (filter.shipmentStatus) params.shipmentStatus = filter.shipmentStatus;
  if (filter.country) params.country = filter.country;
  if (filter.hasNote != null) params.hasNote = filter.hasNote;
  if (filter.search?.trim()) params.search = filter.search.trim();
  return Object.keys(params).length ? params : undefined;
};

const getAll = (filterCriteria: string = "") => {
  return http.get<IAccountManagementCustomerPaginatedResponse>(
    `/account-management-customers${filterCriteria}`
  );
};

const getById = (id: number) => {
  return http.get<IAccountManagementCustomer>(`/account-management-customers/${id}`);
};

const create = (data: { email: string; fullName?: string; mongerlabUserId?: number }) => {
  return http.post<IAccountManagementCustomer>(`/account-management-customers`, data);
};

const update = (
  id: number,
  data: { email?: string; fullName?: string; mongerlabUserId?: number }
) => {
  return http.put<IAccountManagementCustomer>(`/account-management-customers/${id}`, data);
};

const remove = (id: number) => {
  return http.delete(`/account-management-customers/${id}`);
};

const getNotes = (id: number) => {
  return http.get<IAccountManagementCustomerNote[]>(`/account-management-customers/${id}/notes`);
};

const createNote = (id: number, data: { description: string }) => {
  return http.post<IAccountManagementCustomerNote>(`/account-management-customers/${id}/notes`, data);
};

const updateNote = (noteId: number, data: { description: string }) => {
  return http.put<IAccountManagementCustomerNote>(
    `/account-management-customers/notes/${noteId}`,
    data
  );
};

const deleteNote = (noteId: number) => {
  return http.delete(`/account-management-customers/notes/${noteId}`);
};

const getProfitLoss = (id: number, filter?: IAccountManagementProfitLossFilter) => {
  return http.get<IAccountManagementProfitLoss>(`/account-management-customers/${id}/profit-loss`, {
    params: toProfitLossParams(filter),
  });
};

const syncProfitLoss = (id: number, filter?: IAccountManagementProfitLossFilter) => {
  return http.post<IAccountManagementProfitLoss>(
    `/account-management-customers/${id}/profit-loss/sync`,
    null,
    { params: toProfitLossParams(filter) }
  );
};

const updateOrder = (
  orderId: number,
  data: IAccountManagementOrderUpdate,
  filter?: IAccountManagementProfitLossFilter
) => {
  return http.put<IAccountManagementOrderUpdateResponse>(
    `/account-management-customers/orders/${orderId}`,
    data,
    { params: toProfitLossParams(filter) }
  );
};

const getAssignableMembers = () => {
  return http.get<IAccountManagementAssignableMember[]>(
    `/account-management-customers/assignable-members`
  );
};

const assignMember = (id: number, userIds: number[]) => {
  return http.patch<IAccountManagementCustomer>(
    `/account-management-customers/${id}/assign-member`,
    { userIds }
  );
};

const AccountManagementCustomerService = {
  getAll,
  getById,
  create,
  update,
  remove,
  getNotes,
  createNote,
  updateNote,
  deleteNote,
  getProfitLoss,
  syncProfitLoss,
  updateOrder,
  getAssignableMembers,
  assignMember,
};

export default AccountManagementCustomerService;
